import { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  useTheme,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  CalendarToday as CalendarIcon,
  AccountBalanceWalletOutlined as LeaveBalanceIcon,
  EventNote as EventNoteIcon,
  Business as BusinessIcon,
  ManageAccountsOutlined as UserManagementIcon,
  GroupsOutlined as TeamsIcon,
  AccountCircle as AccountCircleIcon,
} from "@mui/icons-material";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout, isManager, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [projectAdminOpen, setProjectAdminOpen] = useState(false);
  const [adminUsersOpen, setAdminUsersOpen] = useState(false);

  // Build menu based on role
  const getMenuItems = () => {
    return [
      { label: "Urenregistratie", icon: <ScheduleIcon />, path: "/timesheet" },
      { label: "Verlofsaldo", icon: <LeaveBalanceIcon />, path: "/leave-balances" },
      { label: "Verlofkaart", icon: <EventNoteIcon />, path: "/leave-card" },
      { label: "Mijn Teams", icon: <TeamsIcon />, path: "/my-project-team" },
      {
        label: "Weekoverzichten",
        icon: <CalendarIcon />,
        path: "/week-overview",
      },
      {
        label: "Rapportages",
        icon: <AssessmentIcon />,
        path: "/reports",
        key: "reports",
        subItems: [
          { label: "Overzicht eigen uren", path: "/reports/own-hours" },
          { label: "Vrije selectie", path: "/reports/free-selection" },
          { label: "Actuele projecten", path: "/projects/active" },
          { label: "Afgeronde projecten", path: "/projects/completed" },
        ].filter(
          (item) =>
            isManager ||
            !["/projects/active", "/projects/completed"].includes(item.path)
        ),
      },
      {
        label: "Projectadministratie",
        icon: <BusinessIcon />,
        path: "/project-admin",
        key: "projectadmin",
        subItems: [
          { label: "Projecten en ramingen", path: "/project-admin" },
          { label: "Nieuw project", path: "/project-admin/new-project" },
          { label: "Opdrachtgevers aanpassen", path: "/project-admin/clients" },
          {
            label: "Activiteiten aanpassen",
            path: "/project-admin/activities",
          },
          {
            label: "Projectrollen aanpassen",
            path: "/project-admin/project-roles",
          },
          {
            label: "Actieve projecten",
            path: "/project-admin/active-projects",
          },
          {
            label: "Inactieve projecten",
            path: "/project-admin/inactive-projects",
          },
        ].filter(
          (item) =>
            isAdmin ||
            ![
              "/project-admin/activities",
            ].includes(item.path)
        ),
      },
      {
        label: "Gebruikers beheer",
        icon: <UserManagementIcon />,
        path: "/admin/users",
        key: "adminusers",
        subItems: [
          { label: "Nieuwe gebruiker", path: "/admin/users/new" },
          { label: "Gebruikersoverzicht", path: "/admin/users" },
          { label: "Laatst ingelogd", path: "/admin/users/last-login" },
        ],
      },
    ].filter(
      (item) =>
        (item.path !== "/week-overview" || isManager) &&
        (item.key !== "projectadmin" || isManager) &&
        (item.key !== "adminusers" || isAdmin)
    );
  };

  const menuItems = getMenuItems();

  useEffect(() => {
    const path = location.pathname;
    setReportsOpen(
      [
        "/reports",
        "/reports/own-hours",
        "/reports/free-selection",
        "/reports/project-teams",
        "/reports/active-projects",
        "/reports/completed-projects",
        "/projects/active",
        "/projects/completed",
      ].includes(path)
    );
    setProjectsOpen(["/projects/active", "/projects/completed"].includes(path));
    setProjectAdminOpen(
      [
        "/project-admin",
        "/project-admin/new-project",
        "/project-admin/clients",
        "/project-admin/activities",
        "/project-admin/project-roles",
        "/project-admin/active-projects",
        "/project-admin/inactive-projects",
      ].includes(path)
    );
    setAdminUsersOpen(
      [
        "/admin/users",
        "/admin/users/new",
        "/admin/users/register",
        "/admin/users/last-login",
      ].includes(path)
    );
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDropdownToggle = (key?: string) => {
    if (key === "reports") {
      setReportsOpen(!reportsOpen);
    } else if (key === "projects") {
      setProjectsOpen(!projectsOpen);
    } else if (key === "projectadmin") {
      setProjectAdminOpen(!projectAdminOpen);
    } else if (key === "adminusers") {
      setAdminUsersOpen(!adminUsersOpen);
    }
  };

  const isDropdownOpen = (key?: string) => {
    if (key === "reports") return reportsOpen;
    if (key === "projects") return projectsOpen;
    if (key === "projectadmin") return projectAdminOpen;
    if (key === "adminusers") return adminUsersOpen;
    return false;
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" component="div">
          Timesheet
        </Typography>
      </Toolbar>
      <List>
        {menuItems.map((item: any) => (
          <Box key={item.path}>
            <ListItem disablePadding>
              <ListItemButton
                selected={
                  item.subItems
                    ? item.subItems.some(
                        (subItem: any) => subItem.path === location.pathname
                      )
                    : location.pathname === item.path
                }
                onClick={() => {
                  if (item.subItems) {
                    handleDropdownToggle(item.key);
                  } else {
                    handleNavigation(item.path);
                  }
                }}
                sx={{
                  justifyContent: "space-between",
                  pr: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Box>
                {item.subItems &&
                  (isDropdownOpen(item.key) ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>
            {item.subItems && (
              <Collapse
                in={isDropdownOpen(item.key)}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {item.subItems.map((subItem: any) => (
                    <ListItemButton
                      key={subItem.path}
                      sx={{ pl: 4 }}
                      selected={location.pathname === subItem.path}
                      onClick={() => handleNavigation(subItem.path)}
                    >
                      <ListItemText primary={subItem.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, minWidth: 0 }}>
            Timesheet - {user ? `${user.firstName} ${user.lastName}` : ""}
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => handleNavigation("/profile")}
            sx={{ mr: 1, flexShrink: 0 }}
            title="Profiel"
          >
            <AccountCircleIcon />
          </IconButton>
          <Button color="inherit" onClick={logout} startIcon={<LogoutIcon />} sx={{ flexShrink: 0 }}>
            Uitloggen
          </Button>
        </Toolbar>
      </AppBar>

      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: 240,
            flexShrink: 0,
            "& .MuiDrawer-paper": { width: 240, boxSizing: "border-box" },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
        <Toolbar />
        <Container maxWidth="xl">{children}</Container>
      </Box>
    </Box>
  );
}
